<script setup lang="ts">
import { orderService } from "@/app/services/OrderService";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { SlimOrder } from "@/types/order/Order";
import { getOptimizedImageUrl, isDataUri } from "@/utils/image";
import { formatNumber } from "@/utils/number";
import { useCustomRouter } from "@/utils/router";
import { computed } from "vue";

const props = defineProps({
  order: {
    type: Object as () => SlimOrder | null,
    default: null
  }
});
const emit = defineEmits<{
  (e: "close"): void;
}>();

const { pushIgnoreError } = useCustomRouter();

const visible = computed(() => !!props.order);
const workDateTime = computed(() => {
  if (!props.order) return "";
  const order = props.order;
  const date = orderService.getFormattedDateWithSlash(order);

  // workTimeOptionsが空の配列の場合の対応
  if (order.workTimeOptions.length === 0) {
    return `${date}`;
  }

  const timeRange = orderService.getWorkTimeRangeFromSlimOrder(
    order.workTimeOptions[0]
  );
  return `${date} ${timeRange}`;
});

const salaryPerDay = computed(() => {
  if (!props.order) return "";
  const order = props.order;

  if (order.workTimeOptions.length === 0) {
    return "";
  }

  return formatNumber(
    orderService.getSalaryPerDayWithVariousAllowancesFromSlimOrder(
      order,
      order.workTimeOptions[0]
    )
  );
});

const hourlyWage = computed(() => {
  if (!props.order) return "";
  const order = props.order;
  return formatNumber(order.hourlyWage);
});

function getOptimizedOrderImageSmall(image: string | null) {
  if (isDataUri(image)) return image;
  if (!image) {
    return require("@/assets/img/undefined.png");
  }
  return getOptimizedImageUrl(image, 480, null);
}

async function onClickDetail() {
  await pushIgnoreError({
    name: "OrdersDetail",
    params: {
      orderId: props.order?.id
    }
  });
}

function onClickClose() {
  emit("close");
}
</script>

<template>
  <BModal
    :visible="visible"
    hide-footer
    title="こちらのお仕事はいかがでしょうか？"
    centered
    @close="onClickClose"
    @hide="onClickClose"
  >
    <div class="d-flex flex-column align-items-center">
      <div v-if="order?.image1" class="order-image" data-testid="order-image">
        <BImgLazy
          class="order-image"
          :src="getOptimizedOrderImageSmall(order?.image1)"
          data-testid="order-image-lazy"
        />
      </div>
      <div class="mt-2 details">
        <p class="font-weight-bold text-center" data-testid="office-name">
          {{ order?.office.name }}
        </p>
        <p class="info-row" data-testid="work-datetime">
          <span class="label">勤務日時:</span>
          <span class="font-weight-bold">{{ workDateTime }}</span>
        </p>
        <p class="info-row">
          <span class="label">日給（時給）: &nbsp;</span>
          <span class="font-weight-bold" data-testid="salary"
            >{{ salaryPerDay }}円（時給 {{ hourlyWage }}円）</span
          >
        </p>
        <p data-testid="office-address">
          <FeatherIcon icon="map-pin" size="14" class="mr-1" />
          {{ order?.office.address }}
        </p>
      </div>
      <AppButton
        block
        class="mt-4"
        @click="onClickDetail"
        data-testid="detail-button"
        >詳細を確認する</AppButton
      >
    </div>
  </BModal>
</template>

<style lang="scss" scoped>
.details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-row {
  display: flex;
  align-items: flex-start;
}

.label {
  min-width: 110px;
}

.salary-per-day {
  color: #d23e36;
}

.order-image {
  width: 100%;
  height: 300px;
  object-fit: cover;
}
</style>
